import { Trophy, Flame, Calendar, TrendingUp, Gift, Crown } from 'lucide-react';
import { useEffect, useState } from 'react';
import API_BASE_URL from '../../config';
import { maskName, maskPhone } from '../../utils/utils';
import StreakShield from './StreakShield';

const LeaderboardSkeleton = ({ hasSponsorship }) => {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Skeleton Podium (Top 3) */}
      <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 items-end ${hasSponsorship ? 'pt-1' : 'pt-4'} pb-2`}>
        {/* 2nd Place Skeleton */}
        <div className="bg-[#181818]/60 border border-[#2b2b2b] rounded-xl p-6 flex flex-col items-center space-y-4 md:h-[220px] justify-end shadow-lg">
          <div className="w-12 h-12 bg-[#222] rounded-full" />
          <div className="w-14 h-14 bg-[#222] rounded-full" />
          <div className="h-4 bg-[#222] rounded w-24" />
          <div className="h-3 bg-[#222] rounded w-16" />
          <div className="flex gap-4 w-full justify-center">
            <div className="h-6 bg-[#222] rounded w-12" />
            <div className="h-6 bg-[#222] rounded w-12" />
          </div>
        </div>

        {/* 1st Place Skeleton */}
        <div className="bg-[#1e1e1e]/60 border border-[#2b2b2b]/80 rounded-xl p-6 flex flex-col items-center space-y-4 md:h-[250px] justify-end shadow-xl">
          <div className="w-14 h-14 bg-[#222] rounded-full" />
          <div className="w-16 h-16 bg-[#222] rounded-full" />
          <div className="h-4 bg-[#222] rounded w-28" />
          <div className="h-3 bg-[#222] rounded w-20" />
          <div className="flex gap-4 w-full justify-center">
            <div className="h-6 bg-[#222] rounded w-16" />
            <div className="h-6 bg-[#222] rounded w-16" />
          </div>
        </div>

        {/* 3rd Place Skeleton */}
        <div className="bg-[#181818]/60 border border-[#2b2b2b] rounded-xl p-6 flex flex-col items-center space-y-4 md:h-[220px] justify-end shadow-lg">
          <div className="w-12 h-12 bg-[#222] rounded-full" />
          <div className="w-14 h-14 bg-[#222] rounded-full" />
          <div className="h-4 bg-[#222] rounded w-24" />
          <div className="h-3 bg-[#222] rounded w-16" />
          <div className="flex gap-4 w-full justify-center">
            <div className="h-6 bg-[#222] rounded w-12" />
            <div className="h-6 bg-[#222] rounded w-12" />
          </div>
        </div>
      </div>

      {/* Skeleton Challenger Table */}
      <div className="bg-[#181818]/60 border border-[#2b2b2b] rounded-xl overflow-hidden shadow-lg mt-6">
        <div className="px-6 py-4 border-b border-[#2b2b2b]">
          <div className="h-5 bg-[#222] rounded w-48" />
        </div>
        <div className="divide-y divide-[#2b2b2b]">
          {[1, 2, 3, 4, 5].map((idx) => (
            <div key={idx} className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4 w-1/2">
                <div className="h-4 bg-[#222] rounded w-6" />
                <div className="w-9 h-9 rounded-full bg-[#222]" />
                <div className="space-y-2 w-2/3">
                  <div className="h-4 bg-[#222] rounded w-2/3" />
                  <div className="h-3 bg-[#222] rounded w-1/2" />
                </div>
              </div>
              <div className="flex gap-6 w-1/3 justify-end">
                <div className="h-6 bg-[#222] rounded w-12" />
                <div className="h-6 bg-[#222] rounded w-12" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ConsistencyScore = ({ onTabChange }) => {
  const [userStats, setUserStats] = useState(null);
  const [rewards, setRewards] = useState([]);
  const userinfo = JSON.parse(localStorage.getItem("fitscan_user"));
  const activeGymcode = localStorage.getItem('fitscan_active_gymcode') || userinfo?.gymcode || '';

  const shouldMask = (member) => {
    if (!member) return false;
    const currentUser = JSON.parse(localStorage.getItem("fitscan_user") || "{}");
    const isSelf = member.memberId === currentUser.id || member.id === currentUser.id || member.username?.toLowerCase() === currentUser.username?.toLowerCase();
    return !isSelf;
  };

  const getDisplayPhone = (member) => {
    if (!member) return '';
    let phone = shouldMask(member) ? (member.maskedPhone || maskPhone(member.username)) : member.username;
    if (phone && phone.startsWith('@')) {
      phone = phone.slice(1);
    }
    return phone;
  };

  const getDisplayName = (member) => {
    if (!member) return '';
    return shouldMask(member) ? (member.maskedName || maskName(member.displayName)) : member.displayName;
  };

  // Leaderboard states
  const [viewMode, setViewMode] = useState('leaderboard'); // default to 'leaderboard'
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState(null);
  const [sponsorshipText, setSponsorshipText] = useState('');

  useEffect(() => {
    const fetchSponsorship = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/settings/leaderboard-sponsorship`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.sponsorshipName) {
            setSponsorshipText(data.sponsorshipName);
          }
        }
      } catch (err) {
        console.error('Error fetching sponsorship:', err);
      }
    };
    fetchSponsorship();
  }, []);

  const fetchLeaderboard = async () => {
    setLeaderboardLoading(true);
    setLeaderboardError(null);
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken') || '{}').token || '';
      if (!userinfo || !accessToken) return;
      const activeGymcode = localStorage.getItem('fitscan_active_gymcode') || userinfo.gymcode;
      const res = await fetch(`${API_BASE_URL}/api/leaderboard/${activeGymcode}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!res.ok) {
        throw new Error('Failed to load leaderboard data.');
      }
      const data = await res.json();
      setLeaderboardData(data);
    } catch (err) {
      console.error(err);
      setLeaderboardError(err.message || 'An error occurred loading leaderboard.');
    } finally {
      setLeaderboardLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'leaderboard') {
      fetchLeaderboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const fetchData = async () => {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken') || '{}').token || '';
      if (!userinfo || !accessToken) return;
      const activeGymcode = localStorage.getItem('fitscan_active_gymcode') || userinfo.gymcode;
      // 1. Get user stats
      const statsRes = await fetch(`${API_BASE_URL}/api/ConsistencyScore/stats`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          Username: userinfo.username,
          Gymcode: activeGymcode
        }),
      });
      const stats = await statsRes.json();
      setUserStats(stats);

      // 3. Get gym rewards
      const rewardsRes = await fetch(`${API_BASE_URL}/api/rewards/gym/rewards`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(activeGymcode),
      });
      const gymRewards = await rewardsRes.json();

      // 4. Award rewards based on streak
      const userRewardsRes = await fetch(`${API_BASE_URL}/api/rewards/user/award`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify(userinfo.username),
      });
      const userRewards = await userRewardsRes.json();

      // 6. Combine rewards with status
      const combinedRewards = userRewards.map((ur) => {
        const gymR = gymRewards.find((gr) => gr.id === ur.gymRewardId);
        return {
          ...gymR,
          id: ur.id,
          awardedOn: ur.awardedOn,
          redeemed: ur.redeemed,
        };
      });

      setRewards(combinedRewards);
    } catch (err) {
      console.error('Error fetching consistency data:', err);
    }
  };



  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 75) return 'text-lime-400';
    if (score >= 50) return 'text-amber-400';
    return 'text-red-400';
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentDays = userStats?.totalVisits ?? 0;
  const pct = Math.min((currentDays / 100) * 100, 100);

  let rewardsMessage = "";
  if (currentDays < 30) {
    rewardsMessage = "Complete 30 days to unlock your first reward!";
  } else if (currentDays >= 30 && currentDays < 60) {
    rewardsMessage = "🎁 30-day reward unlocked! Complete 60 days to earn your next reward.";
  } else if (currentDays >= 60 && currentDays < 100) {
    rewardsMessage = "🎁 60-day reward unlocked! Complete 100 days to earn your final reward.";
  } else {
    rewardsMessage = "🏆 Challenge complete! You've unlocked all rewards.";
  }

  return (
    <div className="space-y-6">


      {/* Header Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#2b2b2b] pb-4">
        <div className="flex items-center gap-3">
          <Trophy size={24} className="text-lime-400" />
          <h1 className="text-2xl font-bold text-white">Gym Consistency</h1>
        </div>

        {/* Tab switcher */}
        <div className="flex bg-[#101010] p-1 rounded-xl border border-[#2b2b2b] self-start sm:self-auto shadow-inner">
          <button
            onClick={() => setViewMode('stats')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${viewMode === 'stats'
              ? 'bg-[#181818] text-[#6CD800] border border-[#2b2b2b] shadow-md'
              : 'text-gray-400 hover:text-gray-200'
              }`}
          >
            My Stats
          </button>
          <button
            onClick={() => setViewMode('leaderboard')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${viewMode === 'leaderboard'
              ? 'bg-[#181818] text-[#6CD800] border border-[#2b2b2b] shadow-md'
              : 'text-gray-400 hover:text-gray-200'
              }`}
          >
            Gym Leaderboard
          </button>
        </div>
      </div>

      <div className="relative w-full">
        {/* My Stats Tab Content */}
        <div className={`transition-opacity duration-200 ease-in-out ${viewMode === 'stats'
          ? 'opacity-100'
          : 'opacity-0 pointer-events-none absolute top-0 left-0 w-full'
          }`}>
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-[#181818] to-[#101010] border border-[#2b2b2b] rounded-xl p-6">
              <div className="flex flex-col items-center">
                <div className="relative w-48 h-48 mb-6">
                  <div className="absolute inset-0 rounded-full border-[12px] border-[#2b2b2b]" />
                  <div
                    className={`absolute inset-0 rounded-full border-[12px] ${getScoreColor(userStats?.score ?? 0)}`}
                    style={{
                      clipPath: `polygon(0 0, 50% 0, 50% 100%, 0 100%)`,
                      transform: `rotate(${(userStats?.score ?? 0) * 3.6}deg)`,
                    }}
                  ></div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-4xl font-bold ${getScoreColor(userStats?.score ?? 0)}`}>{userStats?.score ?? 0}</span>
                    <span className="text-gray-400">out of 100</span>
                  </div>
                </div>

                <h3 className={`text-xl font-semibold mb-2 ${getScoreColor(userStats?.score ?? 0)}`}>
                  {userStats?.motivationalMessage ?? ''}
                </h3>
              </div>
            </div>

            {/* Metrics cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Flame size={20} className="text-red-400" />
                  <h3 className="font-medium text-white">Total Visits</h3>
                </div>
                <p className="text-3xl font-bold mt-2 text-white">{userStats?.totalVisits ?? 0} days</p>
                <p className="text-sm text-gray-400 mt-1">
                  Last visit: {(() => {
                    if (!userStats?.lastVisitDate) return 'NA';
                    const date = new Date(userStats.lastVisitDate);
                    if (isNaN(date) || date.getFullYear() === 1) return 'NA';
                    return date.toLocaleDateString('en-GB'); // 01/01/2025 format
                  })()}
                </p>
              </div>

              <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Calendar size={20} className="text-blue-400" />
                  <h3 className="font-medium text-white">Weekly Average</h3>
                </div>
                <p className="text-3xl font-bold mt-2 text-white">{userStats?.weeklyAverage ?? 0} visits</p>
                <p className="text-sm text-gray-400 mt-1">
                  {(userStats?.weeklyAverage ?? 0) >= 3 ? "Great frequency!" : "Try for 3+ visits/week"}
                </p>
              </div>

              <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <TrendingUp size={20} className="text-lime-400" />
                  <h3 className="font-medium text-white">Monthly Visits</h3>
                </div>
                <p className="text-3xl font-bold mt-2 text-white">{userStats?.monthlyVisits ?? 0}</p>
                <p className="text-sm text-gray-400 mt-1">
                  {(userStats?.monthlyVisits ?? 0) >= 12 ? "Excellent commitment!" : "Keep going strong!"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Gym Leaderboard Tab Content */}
        <div className={`transition-opacity duration-200 ease-in-out ${viewMode === 'leaderboard'
          ? 'opacity-100'
          : 'opacity-0 pointer-events-none absolute top-0 left-0 w-full'
          }`}>
          <div className="space-y-6">
            {sponsorshipText && (
              <div className="text-center py-2 bg-[#6CD800]/10 border border-[#6CD800]/20 rounded-xl text-[#6CD800] text-xs font-bold uppercase tracking-wider shadow-sm mt-4">
                Sponsored by {sponsorshipText}
              </div>
            )}
            <div className={`flex flex-col space-y-5 ${sponsorshipText ? "mt-4" : ""}`}>
              {leaderboardLoading ? (
                <LeaderboardSkeleton hasSponsorship={!!sponsorshipText} />
              ) : leaderboardError ? (
                <div className="bg-[#181818] border border-red-500/20 text-red-400 p-6 rounded-xl text-center space-y-4 max-w-md mx-auto shadow-lg">
                  <p>{leaderboardError}</p>
                  <button
                    onClick={fetchLeaderboard}
                    className="px-4 py-2 bg-[#6CD800]/10 border border-[#6CD800]/40 text-[#6CD800] rounded-lg text-sm hover:bg-[#6CD800]/20 transition-all font-semibold"
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <>
                  {leaderboardData?.isPlaceholder ? (
                    <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-8 text-center space-y-4 max-w-md mx-auto shadow-lg">
                      <Calendar size={48} className="text-gray-500 mx-auto" />
                      <h2 className="text-lg font-bold text-white">Leaderboard Not Active</h2>
                      <p className="text-sm text-gray-400 leading-relaxed">
                        {leaderboardData.message}
                      </p>
                      <div className="text-xs text-[#6CD800] bg-[#6CD800]/10 border border-[#6CD800]/20 rounded-lg p-2.5 max-w-xs mx-auto">
                        ⏰ Qualification: Reach 14 membership days and 4 attendance days this month!
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Podium (Top 3) */}
                      <div className="flex flex-col items-center w-full px-2 sm:px-4">
                        <div className="flex items-end justify-center gap-2 sm:gap-4 w-full max-w-2xl pt-4 pb-1">
                          {/* 2nd Place Card */}
                          {(() => {
                            const top3 = leaderboardData?.podium || [];
                            const second = top3[1];
                            if (!second) return <div className="flex-1 max-w-[110px] sm:max-w-[180px]" />;
                            return (
                              <div
                                className="bg-[#141414] border border-[#252525] rounded-[10px] p-2 sm:p-4 flex flex-col items-center space-y-2 sm:space-y-3 shadow-lg transition-transform hover:scale-[1.02] flex-1 max-w-[110px] sm:max-w-[180px]"
                                style={{ paddingTop: '16px' }}
                              >
                                {/* Rank badge circle at the top */}
                                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#a0a0a0] text-[#0B0F19] font-black text-[10px] sm:text-xs flex items-center justify-center">
                                  2
                                </div>
                                {/* Avatar circle with initials or photo */}
                                {second.photoUrl ? (
                                  <img src={second.photoUrl.replace(/\\/g, '/')} alt="" className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border border-[#404040]" />
                                ) : (
                                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#1A1A1A] text-gray-400 border border-[#404040] flex items-center justify-center text-base sm:text-lg font-bold">
                                    {second.displayName?.[0]?.toUpperCase() || 'U'}
                                  </div>
                                )}
                                <div className="text-center w-full">
                                  <h4 className="font-bold text-white text-[10px] sm:text-sm line-clamp-1">{getDisplayName(second)}</h4>
                                  <p className="text-[8px] sm:text-[10px] text-gray-500 mt-0.5 line-clamp-1">@{getDisplayPhone(second)}</p>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:gap-4 items-center mt-1 w-full justify-center text-center">
                                  <div className="flex flex-col items-center">
                                    <span className="hidden sm:block text-[9px] text-gray-500">Consistency</span>
                                    <span className="text-[10px] sm:text-sm font-extrabold text-[#6CD800]">{second.consistencyScore}%</span>
                                  </div>
                                  <div className="hidden sm:block w-px h-6 bg-[#252525]" />
                                  <div className="flex flex-col items-center justify-center">
                                    <span className="hidden sm:block text-[9px] text-gray-500">Streak</span>
                                    <span className="text-[10px] sm:text-sm font-extrabold text-white flex items-center justify-center gap-0.5">
                                      <Flame size={12} className="text-[#FF6B35] fill-[#FF6B35]" /> {second.currentStreak}d
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* 1st Place Card (Gold/Green) */}
                          {(() => {
                            const top3 = leaderboardData?.podium || [];
                            const first = top3[0];
                            if (!first) return <div className="flex-1 max-w-[120px] sm:max-w-[190px]" />;
                            return (
                              <div
                                className="bg-[#141414] border-[1.5px] border-[#6CD800] rounded-[10px] p-2 sm:p-4 flex flex-col items-center space-y-2 sm:space-y-3 shadow-xl shadow-[#6CD800]/5 transition-transform hover:scale-[1.02] flex-1 max-w-[120px] sm:max-w-[190px]"
                                style={{ paddingTop: '26px', marginBottom: '10px' }}
                              >
                                {/* Rank badge circle at the top */}
                                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#6CD800] text-[#0B0F19] font-black text-[10px] sm:text-xs flex items-center justify-center">
                                  1
                                </div>
                                {/* Avatar circle with initials or photo */}
                                {first.photoUrl ? (
                                  <img src={first.photoUrl.replace(/\\/g, '/')} alt="" className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-[#6CD800]" />
                                ) : (
                                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#1A2E05] text-[#6CD800] border-2 border-[#6CD800] flex items-center justify-center text-lg sm:text-xl font-bold">
                                    {first.displayName?.[0]?.toUpperCase() || 'U'}
                                  </div>
                                )}
                                <div className="text-center w-full">
                                  <h4 className="font-extrabold text-white text-[11px] sm:text-base line-clamp-1">{getDisplayName(first)}</h4>
                                  <p className="text-[8px] sm:text-[10px] text-gray-500 mt-0.5 line-clamp-1">@{getDisplayPhone(first)}</p>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:gap-4 items-center mt-1 w-full justify-center text-center">
                                  <div className="flex flex-col items-center">
                                    <span className="hidden sm:block text-[9px] text-gray-500">Consistency</span>
                                    <span className="text-[10px] sm:text-sm font-extrabold text-[#6CD800]">{first.consistencyScore}%</span>
                                  </div>
                                  <div className="hidden sm:block w-px h-6 bg-[#252525]" />
                                  <div className="flex flex-col items-center justify-center">
                                    <span className="hidden sm:block text-[9px] text-gray-500">Streak</span>
                                    <span className="text-[10px] sm:text-sm font-extrabold text-white flex items-center justify-center gap-0.5">
                                      <Flame size={12} className="text-[#FF6B35] fill-[#FF6B35]" /> {first.currentStreak}d
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* 3rd Place Card */}
                          {(() => {
                            const top3 = leaderboardData?.podium || [];
                            const third = top3[2];
                            if (!third) return <div className="flex-1 max-w-[110px] sm:max-w-[180px]" />;
                            return (
                              <div
                                className="bg-[#141414] border border-[#252525] rounded-[10px] p-2 sm:p-4 flex flex-col items-center space-y-2 sm:space-y-3 shadow-lg transition-transform hover:scale-[1.02] flex-1 max-w-[110px] sm:max-w-[180px]"
                                style={{ paddingTop: '8px' }}
                              >
                                {/* Rank badge circle at the top */}
                                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#cd7f32] text-[#0B0F19] font-black text-[10px] sm:text-xs flex items-center justify-center">
                                  3
                                </div>
                                {/* Avatar circle with initials or photo */}
                                {third.photoUrl ? (
                                  <img src={third.photoUrl.replace(/\\/g, '/')} alt="" className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border border-[#404040]" />
                                ) : (
                                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#1A1A1A] text-gray-400 border border-[#404040] flex items-center justify-center text-base sm:text-lg font-bold">
                                    {third.displayName?.[0]?.toUpperCase() || 'U'}
                                  </div>
                                )}
                                <div className="text-center w-full">
                                  <h4 className="font-bold text-white text-[10px] sm:text-sm line-clamp-1">{getDisplayName(third)}</h4>
                                  <p className="text-[8px] sm:text-[10px] text-gray-500 mt-0.5 line-clamp-1">@{getDisplayPhone(third)}</p>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:gap-4 items-center mt-1 w-full justify-center text-center">
                                  <div className="flex flex-col items-center">
                                    <span className="hidden sm:block text-[9px] text-gray-500">Consistency</span>
                                    <span className="text-[10px] sm:text-sm font-extrabold text-[#6CD800]">{third.consistencyScore}%</span>
                                  </div>
                                  <div className="hidden sm:block w-px h-6 bg-[#252525]" />
                                  <div className="flex flex-col items-center justify-center">
                                    <span className="hidden sm:block text-[9px] text-gray-500">Streak</span>
                                    <span className="text-[10px] sm:text-sm font-extrabold text-white flex items-center justify-center gap-0.5">
                                      <Flame size={12} className="text-[#FF6B35] fill-[#FF6B35]" /> {third.currentStreak}d
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        {/* 3-Step Visual Podium Bar */}
                        <div className="flex items-end justify-center gap-2 sm:gap-4 w-full max-w-2xl mt-1 mb-6">
                          <div className="h-[10px] flex-1 max-w-[110px] sm:max-w-[180px] bg-[#252525] rounded-t-sm" />
                          <div className="h-[18px] flex-1 max-w-[120px] sm:max-w-[190px] bg-[#6CD800] rounded-t-sm" />
                          <div className="h-[6px] flex-1 max-w-[110px] sm:max-w-[180px] bg-[#252525] rounded-t-sm" />
                        </div>
                      </div>

                      {/* Ranks 4-10 Table */}
                      {leaderboardData?.rankings?.length > 0 && (
                        <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl overflow-hidden shadow-lg">
                          <div className="px-6 py-4 border-b border-[#2b2b2b]">
                            <h3 className="text-base font-bold text-white">Top Challenger Rankings</h3>
                          </div>
                          <div className="divide-y divide-[#2b2b2b] overflow-x-auto">
                            {leaderboardData.rankings.map((member) => {
                              const isSelf = member.username?.toLowerCase() === userinfo?.username?.toLowerCase();
                              return (
                                <div key={member.memberId} className={`flex items-center justify-between px-6 py-3.5 hover:bg-[#1f1f1f] transition-colors ${isSelf ? 'bg-[#6CD800]/5' : ''}`}>
                                  <div className="flex items-center gap-4">
                                    <span className="text-sm font-extrabold text-gray-500 w-6">#{member.rank}</span>
                                    <div className="w-9 h-9 rounded-full bg-[#222] border border-[#333] overflow-hidden flex-shrink-0">
                                      {member.photoUrl ? (
                                        <img src={member.photoUrl.replace(/\\/g, '/')} alt="" className="w-full h-full object-cover" />
                                      ) : (
                                        <div className="w-full h-full bg-[#6CD800]/10 text-[#6CD800] flex items-center justify-center font-bold text-sm">
                                          {member.displayName?.[0]?.toUpperCase() || 'U'}
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <span className={`text-sm font-bold block ${isSelf ? 'text-[#6CD800]' : 'text-gray-200'}`}>
                                        {getDisplayName(member)} {isSelf && <span className="text-[10px] bg-[#6CD800]/20 text-[#6CD800] px-1.5 py-0.5 rounded ml-1.5 font-normal">You</span>}
                                      </span>
                                      <span className="text-[10px] text-gray-500">@{getDisplayPhone(member)}</span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-6 text-right">
                                    <div>
                                      <span className="text-[10px] text-gray-500 block">Consistency</span>
                                      <span className="text-sm font-extrabold text-gray-300">{member.consistencyScore}%</span>
                                    </div>
                                    <div>
                                      <span className="text-[10px] text-gray-500 block">Streak</span>
                                      <span className="text-sm font-extrabold text-orange-400">🔥 {member.currentStreak}d</span>
                                    </div>
                                    <div className="hidden sm:block">
                                      <span className="text-[10px] text-gray-500 block">Lifetime Scans</span>
                                      <span className="text-sm font-semibold text-gray-400">{member.lifetimeCheckins}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Streak Shield Component */}
                  <StreakShield gymcode={activeGymcode} onShieldActivated={fetchData} />

                  {/* 🎯 Milestone Progress Line */}
                  <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-200 mb-4 text-center">
                      Milestone Progress
                    </h3>
                    <div className="relative pt-10">
                      <div className="relative">
                        <div className="h-4 bg-[#2b2b2b] rounded-full overflow-hidden">
                          <div
                            className="bg-lime-400 h-full transition-all duration-700 rounded-full"
                            style={{
                              width: `${Math.min(
                                ((userStats?.totalVisits ?? 0) / (100)) * 100,
                                100
                              )}%`,
                            }}
                          >
                            <div className="absolute top-[40%] left-0 right-0">
                              {[0, 30, 60, 100].map((day) => {
                                const reward = rewards.find(r => r.streakDay === day);
                                const achieved = (userStats?.totalVisits ?? 0) >= day;
                                const isRedeemed = reward?.redeemed;
                                const leftPos = `${(day / 100) * 100}%`;
                                return (
                                  <div key={day} style={{ width: '0', left: leftPos }} className="absolute -translate-x-1/2">
                                    <div className="flex flex-col items-center -mt-12">
                                      {/* Day Label */}
                                      <span className="text-xs text-gray-400 font-medium mb-1">
                                        {day === 0 ? '0day' : `${day}days`}
                                      </span>

                                      {/* Icon */}
                                      <div className="mb-1 animate-pop">
                                        {reward?.rewardType === 'FREE_MONTH' ? (
                                          <Crown size={18} className={achieved ? "text-amber-400" : "text-gray-500"} />
                                        ) : (
                                          <Gift size={18} className={achieved ? "text-lime-400" : "text-gray-500"} />
                                        )}
                                      </div>

                                      {/* Dot */}
                                      <div
                                        className={`w-4 h-4 rounded-full border-2 ${isRedeemed
                                          ? 'bg-green-500 border-green-400'
                                          : achieved
                                            ? 'bg-lime-400 border-lime-300'
                                            : 'bg-[#121212] border-gray-500'
                                          }`}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Day indicator dot on the leading edge of the progress bar */}
                        <div
                          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-[11px] h-[11px] rounded-full bg-[#6CD800] z-20 pointer-events-none"
                          style={{ left: `calc(${pct}% - 1px)` }}
                        />

                        {/* Day indicator bubble above the dot */}
                        {currentDays > 0 && (
                          <div
                            className="absolute bottom-full mb-3 -translate-x-1/2 z-30 flex flex-col items-center pointer-events-none"
                            style={{
                              left: currentDays >= 98
                                ? `min(calc(${pct}% - 1px), calc(100% - 28px))`
                                : `calc(${pct}% - 1px)`
                            }}
                          >
                            <div className="bg-[#6CD800] text-black text-[11px] font-bold px-[7px] py-[2px] rounded-[10px] whitespace-nowrap shadow-md">
                              Day {currentDays}
                            </div>
                            <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-[#6CD800] -mt-[1px]" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Reward Section Banner */}
                  <div
                    onClick={() => onTabChange?.('rewards')}
                    className="bg-gradient-to-br from-[#1c2912] to-[#10150a] border border-[#6CD800]/20 rounded-xl p-6 cursor-pointer hover:border-[#6CD800]/40 transition-all duration-300 group shadow-lg shadow-[#6CD800]/2 relative overflow-hidden"
                  >
                    {/* Subtle background glow */}
                    <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-[#6CD800]/5 rounded-full blur-3xl group-hover:bg-[#6CD800]/10 transition-all duration-500" />

                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative z-10">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Gift size={20} className="text-[#6CD800] animate-pulse" />
                          <h3 className="text-lg font-bold text-white tracking-wide">Consistency Milestone Rewards</h3>
                        </div>
                        <p className="text-sm text-gray-300 max-w-2xl leading-relaxed">
                          {rewardsMessage}
                        </p>
                        <p className="text-xs text-gray-400 max-w-2xl leading-relaxed mt-1">
                          Scratch cards, FitScan coins, sponsor discounts, and free membership months are waiting for you.
                          You have completed <span className="text-[#6CD800] font-black">{currentDays} days</span> of your consistency challenge!
                        </p>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onTabChange?.('rewards');
                        }}
                        className="px-5 py-2.5 bg-[#6CD800] hover:bg-[#5bc200] text-[#0B0F19] font-black text-xs uppercase tracking-wider rounded-lg transition-colors flex items-center gap-1.5 shadow-md shadow-[#6CD800]/10 self-stretch md:self-auto text-center justify-center cursor-pointer"
                      >
                        View & Claim Rewards
                        <span className="group-hover:translate-x-1 transition-transform inline-block">➔</span>
                      </button>
                    </div>
                  </div>

                  {/* Sticky bottom status banner */}
                  {!leaderboardData?.isPlaceholder && leaderboardData?.currentUserStatus && (
                    <div className="bg-gradient-to-r from-[#1e1e1e] to-[#161616] border-l-4 border-l-[#6CD800] border border-[#2b2b2b] p-5 rounded-r-xl shadow-lg">
                      {leaderboardData.currentUserStatus.isEligible ? (
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 text-white font-bold text-lg">
                              {leaderboardData.currentUserStatus.exactRank ? (
                                <>
                                  {leaderboardData.currentUserStatus.exactRank === 1 ? '🥇' :
                                    leaderboardData.currentUserStatus.exactRank === 2 ? '🥈' :
                                      leaderboardData.currentUserStatus.exactRank === 3 ? '🥉' : '🏆'}
                                  {" "}You are ranked #{leaderboardData.currentUserStatus.exactRank}
                                </>
                              ) : (
                                <>
                                  🏆 You're {leaderboardData.currentUserStatus.positionsFromTop10} {leaderboardData.currentUserStatus.positionsFromTop10 === 1 ? 'position' : 'positions'} away from the Top 10.
                                </>
                              )}
                            </div>
                            {/* Bronze Gap Message */}
                            {leaderboardData.currentUserStatus.gapCase && (
                              <p className="text-sm text-gray-400 mt-1">
                                {leaderboardData.currentUserStatus.gapCase === 'A' ? (
                                  <span>🎯 Progress target: You are <span className="text-[#6CD800] font-bold">{leaderboardData.currentUserStatus.gapToBronze}%</span> away from Bronze.</span>
                                ) : (
                                  <span>🔥 Streak target: Extend your streak by <span className="text-[#6CD800] font-bold">{leaderboardData.currentUserStatus.gapToBronze}</span> more day{leaderboardData.currentUserStatus.gapToBronze === 1 ? '' : 's'} to match Bronze.</span>
                                )}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-center bg-[#101010] px-4 py-2 border border-[#2b2b2b] rounded-lg">
                              <span className="text-[10px] text-gray-500 block uppercase tracking-wide">My Score</span>
                              <span className="text-base font-extrabold text-[#6CD800]">{leaderboardData.currentUserStatus.consistencyScore}%</span>
                            </div>
                            <div className="text-center bg-[#101010] px-4 py-2 border border-[#2b2b2b] rounded-lg">
                              <span className="text-[10px] text-gray-500 block uppercase tracking-wide">My Streak</span>
                              <span className="text-base font-extrabold text-orange-500">🔥 {leaderboardData.currentUserStatus.currentStreak}d</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="text-white font-bold flex items-center gap-2">
                            ⚠️ You are not currently ranked on the gym leaderboard.
                          </div>
                          <p className="text-sm text-gray-400 leading-relaxed">
                            Leaderboard ranks require members to have a minimum of <strong>14 membership days</strong> and <strong>4 attendance days</strong> in the current month.
                          </p>
                          <div className="bg-[#101010] p-3 rounded-lg border border-[#2b2b2b] text-xs text-gray-300 inline-flex flex-wrap gap-x-6 gap-y-2 mt-1">
                            <div>My Attendance Days: <span className="text-[#6CD800] font-bold">{leaderboardData.currentUserStatus.attendanceDays}</span> / 4</div>
                            <div>My Membership Days: <span className="text-[#6CD800] font-bold">{leaderboardData.currentUserStatus.membershipDays}</span> / 14</div>
                            <div>My Consistency Score: <span className="text-[#6CD800] font-bold">{leaderboardData.currentUserStatus.consistencyScore}%</span></div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsistencyScore;
